import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { formatCurrency } from '../../lib/utils';
import { createPaymentIntent, processPayment } from '../../lib/stripe';

interface PaymentFormProps {
  invoiceId: string;
  invoiceNumber: string;
  amount: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  invoiceId,
  invoiceNumber,
  amount,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'upi' | 'bank_transfer'>('card');

  // Card styling
  const cardStyle = {
    style: {
      base: {
        color: '#32325d',
        fontFamily: '"Inter", sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }
    
    if (paymentMethod !== 'card') {
      // For non-card payments, we'll use a different flow
      await handleAlternativePayment();
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // In a real implementation, we would:
      // 1. Create a payment intent on the server
      // 2. Confirm the payment with stripe.confirmCardPayment
      
      // For demo purposes, we'll simulate a successful payment
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Process the payment
      await processPayment(invoiceId, amount);
      
      // Show success state
      setPaymentSuccess(true);
      
      // Notify parent component
      setTimeout(() => {
        onSuccess();
      }, 1500);
      
    } catch (err: any) {
      setError(err.message || 'An error occurred while processing your payment.');
    } finally {
      setProcessing(false);
    }
  };

  const handleAlternativePayment = async () => {
    setProcessing(true);
    setError(null);

    try {
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (paymentMethod === 'upi') {
        // In a real app, we would generate a UPI payment link or QR code
        alert('In a real implementation, a UPI payment screen would appear here.');
      } else if (paymentMethod === 'bank_transfer') {
        // In a real app, we would show bank details
        alert('In a real implementation, bank transfer details would be shown here.');
      }
      
      // Process the payment
      await processPayment(invoiceId, amount);
      
      // Show success state
      setPaymentSuccess(true);
      
      // Notify parent component
      setTimeout(() => {
        onSuccess();
      }, 1500);
      
    } catch (err: any) {
      setError(err.message || 'An error occurred while processing your payment.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    setError(event.error ? event.error.message : '');
  };

  if (paymentSuccess) {
    return (
      <Card className="p-8 text-center">
        <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-success-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
        <p className="text-gray-600 mb-6">
          Your payment of {formatCurrency(amount)} for invoice {invoiceNumber} has been processed successfully.
        </p>
        <p className="text-sm text-gray-500 mb-4">
          A receipt has been sent to your email address.
        </p>
        <Button
          onClick={onSuccess}
          className="bg-success-600 hover:bg-success-700"
        >
          Return to Invoices
        </Button>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && <Alert type="error" message={error} />}
      
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-blue-700">Invoice</p>
              <p className="font-medium text-blue-900">{invoiceNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-blue-700">Amount</p>
              <p className="text-xl font-bold text-blue-900">{formatCurrency(amount)}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <button
            type="button"
            onClick={() => setPaymentMethod('card')}
            className={`p-4 border rounded-lg flex flex-col items-center justify-center transition-colors ${
              paymentMethod === 'card' 
                ? 'border-primary-500 bg-primary-50' 
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <CreditCard className={`w-6 h-6 mb-2 ${paymentMethod === 'card' ? 'text-primary-600' : 'text-gray-500'}`} />
            <span className={`text-sm font-medium ${paymentMethod === 'card' ? 'text-primary-700' : 'text-gray-700'}`}>Card</span>
          </button>
          
          <button
            type="button"
            onClick={() => setPaymentMethod('upi')}
            className={`p-4 border rounded-lg flex flex-col items-center justify-center transition-colors ${
              paymentMethod === 'upi' 
                ? 'border-primary-500 bg-primary-50' 
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className={`w-6 h-6 mb-2 flex items-center justify-center ${paymentMethod === 'upi' ? 'text-primary-600' : 'text-gray-500'}`}>
              UPI
            </div>
            <span className={`text-sm font-medium ${paymentMethod === 'upi' ? 'text-primary-700' : 'text-gray-700'}`}>UPI</span>
          </button>
          
          <button
            type="button"
            onClick={() => setPaymentMethod('bank_transfer')}
            className={`p-4 border rounded-lg flex flex-col items-center justify-center transition-colors ${
              paymentMethod === 'bank_transfer' 
                ? 'border-primary-500 bg-primary-50' 
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className={`w-6 h-6 mb-2 flex items-center justify-center ${paymentMethod === 'bank_transfer' ? 'text-primary-600' : 'text-gray-500'}`}>
              Bank
            </div>
            <span className={`text-sm font-medium ${paymentMethod === 'bank_transfer' ? 'text-primary-700' : 'text-gray-700'}`}>Bank Transfer</span>
          </button>
        </div>
      </div>
      
      {paymentMethod === 'card' && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Card Information</h3>
          <div className="border border-gray-300 rounded-lg p-4 mb-4">
            <CardElement 
              options={cardStyle} 
              onChange={handleCardChange}
            />
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="flex items-start space-x-3">
              <div className="mt-1">
                <AlertCircle className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  This is a demo implementation. In a real application, your card information would be securely processed by Stripe.
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  For testing, you can use Stripe's test card: 4242 4242 4242 4242, any future date, any 3 digits for CVC, and any postal code.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {paymentMethod === 'upi' && (
        <div className="bg-gray-50 p-6 rounded-lg text-center">
          <p className="text-gray-700 mb-4">
            In a real implementation, a UPI QR code or payment link would be displayed here.
          </p>
          <div className="w-48 h-48 bg-white border border-gray-300 rounded-lg mx-auto mb-4 flex items-center justify-center">
            <p className="text-gray-500">UPI QR Code</p>
          </div>
          <p className="text-sm text-gray-600">
            Scan with any UPI app to complete payment
          </p>
        </div>
      )}
      
      {paymentMethod === 'bank_transfer' && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Bank Account Details</h4>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Account Name:</span>
              <span className="font-medium">AI CRM Business Solutions</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Account Number:</span>
              <span className="font-medium">1234567890</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">IFSC Code:</span>
              <span className="font-medium">ABCD0001234</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Bank:</span>
              <span className="font-medium">Example Bank</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Please use invoice number {invoiceNumber} as the payment reference.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
            <p className="text-sm text-yellow-700">
              After making the transfer, click "Complete Payment" to mark this invoice as paid.
            </p>
          </div>
        </div>
      )}
      
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          loading={processing}
          disabled={processing || (paymentMethod === 'card' && !cardComplete)}
          className="bg-success-600 hover:bg-success-700"
        >
          {processing ? 'Processing...' : `Pay ${formatCurrency(amount)}`}
        </Button>
      </div>
    </form>
  );
};